import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CognitoUser, CognitoUserPool, AuthenticationDetails, CognitoUserSession, ChallengeName } from 'amazon-cognito-identity-js';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, of, from, switchMap, catchError, throwError, map } from 'rxjs';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { User } from 'shared_models';
import { ApiResponse } from 'shared_models';
import { ScreenOrientation } from '@capacitor/screen-orientation';

interface ChallengeParameters {
  userAttributes: { [key: string]: string };
  requiredAttributes: string[];
}

interface CognitoUserWithChallenge extends CognitoUser {
  challengeParameters?: ChallengeParameters;
  challengeName?: ChallengeName;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userPool: CognitoUserPool;
  private currentUser: User | null = null;
  private currentCognitoUser: CognitoUserWithChallenge | null = null;
  private sessionCheckInterval: any;
  private readonly SESSION_CHECK_INTERVAL = 60000; // Check every minute
  private authState = new BehaviorSubject<boolean>(false);
  private lastTokenRefreshTime: number = 0;
  protected newPasswordRequiredUser: CognitoUserWithChallenge | null = null;
  private readonly tokenRefreshInterval = 50 * 60 * 1000; // 50 minutes
  private readonly FIRST_TIME_LOGIN_TIMEOUT = 30 * 60 * 1000; // 30 minutes timeout
  private firstTimeLoginStartTime: number = 0;

  constructor(
    private router: Router,
    private http: HttpClient
  ) {
    console.log('Initializing AuthService with config:', {
      UserPoolId: environment.cognitoConfig.UserPoolId,
      ClientId: environment.cognitoConfig.ClientId,
      Region: environment.cognitoConfig.Region
    });
    
    this.userPool = new CognitoUserPool({
      UserPoolId: environment.cognitoConfig.UserPoolId,
      ClientId: environment.cognitoConfig.ClientId
    });

    // Start session monitoring
    this.startSessionMonitoring();
    this.setupTokenRefresh();
  }

  private startSessionMonitoring() {
    // Clear any existing interval
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }

    // Set up periodic session check
    this.sessionCheckInterval = setInterval(async () => {
      await this.checkSession();
    }, this.SESSION_CHECK_INTERVAL);
  }

  private pauseSessionMonitoring() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }

  private resumeSessionMonitoring() {
    if (!this.sessionCheckInterval) {
      this.startSessionMonitoring();
    }
  }

  private clearFirstTimeLoginState() {
    this.firstTimeLoginStartTime = 0;
    this.newPasswordRequiredUser = null;
    sessionStorage.removeItem('NEW_PASSWORD_REQUIRED');
    sessionStorage.removeItem('USER_ATTRIBUTES');
  }

  private async checkSession() {
    try {
      // Don't check session if user is in new password required flow
      const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
      if (isNewPasswordRequired) {
        console.log('Session check skipped - user is in new password required flow');
        
        // Check if first-time login has timed out
        if (this.firstTimeLoginStartTime > 0) {
          const timeElapsed = Date.now() - this.firstTimeLoginStartTime;
          if (timeElapsed > this.FIRST_TIME_LOGIN_TIMEOUT) {
            console.log('First-time login timed out, clearing state');
            this.clearFirstTimeLoginState();
            this.handleSessionExpired();
          }
        }
        return;
      }

      // Don't check session if we don't have a current user
      if (!this.currentCognitoUser) {
        console.log('Session check skipped - no current Cognito user');
        return;
      }

      const session = await this.getCurrentSession();
      if (!session) {
        this.handleSessionExpired();
        return;
      }

      // Check if the access token is about to expire (within 5 minutes)
      const expirationTime = session.getAccessToken().getExpiration() * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;

      if (timeUntilExpiration <= 300000) { // 5 minutes in milliseconds
        console.log('Token expiring soon, refreshing session...');
        await this.refreshSession();
      }
    } catch (error) {
      console.error('Error checking session:', error);
      // Don't immediately logout on session check errors during first-time login
      const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
      if (!isNewPasswordRequired) {
        this.handleSessionExpired();
      }
    }
  }

  private async refreshSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        reject(new Error('No current user'));
        return;
      }

      this.currentCognitoUser?.getSession((err: any, session: CognitoUserSession | null) => {
        if (err) {
          console.error('Error refreshing session:', err);
          this.handleSessionExpired();
          reject(err);
          return;
        }
        
        // Only log token refresh if it's been more than 5 minutes since the last refresh
        const currentTime = Date.now();
        if (currentTime - this.lastTokenRefreshTime > 300000) {
          console.log('Session refreshed successfully');
          this.lastTokenRefreshTime = currentTime;
        }
        
        resolve();
      });
    });
  }

  private handleSessionExpired() {
    console.log('Session expired, logging out user');
    this.authState.next(false);
    this.logout();
  }

  login(username: string, password: string): Promise<boolean> {
    console.group('🔐 AuthService - login');
    console.log('Attempting login for user:', username);
    
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });

      // Set the authentication flow type to USER_PASSWORD_AUTH
      cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

      const authCallbacks = {
        onSuccess: async (session: CognitoUserSession) => {
          console.log('=== Authentication Success ===');
          this.currentCognitoUser = cognitoUser;
          this.authState.next(true);
          this.lastTokenRefreshTime = Date.now();
          
          try {
            const user = await this.getCurrentUser().toPromise();
            this.currentUser = user || null;
            console.log('User fetched successfully:', this.currentUser);
            console.groupEnd();
            resolve(true);
          } catch (error) {
            console.error('Error fetching user after login:', error);
            console.groupEnd();
            reject(error);
          }
        },
        onFailure: (err: Error) => {
          console.log('=== Authentication Failure ===');
          console.error('Error details:', err);
          console.error('Error message:', err.message);
          console.error('Error stack:', err.stack);
          console.groupEnd();
          
          // Ensure we're not authenticated on failure
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          reject(err);
        },
        newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
          console.log('=== New Password Required ===');
          console.log('User attributes:', userAttributes);
          console.log('Required attributes:', requiredAttributes);
          
          // Pause session monitoring during first-time login
          this.pauseSessionMonitoring();
          
          // Set timeout for first-time login
          this.firstTimeLoginStartTime = Date.now();
          
          // Store the user requiring password change
          this.newPasswordRequiredUser = cognitoUser;
          console.log('Set newPasswordRequiredUser:', this.newPasswordRequiredUser);
          
          // Store the user attributes for later use
          sessionStorage.setItem('NEW_PASSWORD_REQUIRED', 'true');
          sessionStorage.setItem('USER_ATTRIBUTES', JSON.stringify(userAttributes));
          
          // Ensure we're not authenticated yet
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          // Use setTimeout to ensure navigation happens after current execution cycle
          setTimeout(() => {
            console.log('Attempting navigation to set-initial-password');
            this.router.navigate(['/set-initial-password'], { 
              replaceUrl: true,
              skipLocationChange: true 
            }).then(success => {
              console.log('Navigation result:', success);
              if (!success) {
                console.error('Navigation failed, trying alternative approach');
                // Try alternative navigation if the first attempt fails
                this.router.navigateByUrl('/set-initial-password', { 
                  replaceUrl: true,
                  skipLocationChange: true 
                });
              }
            }).catch(error => {
              console.error('Navigation error:', error);
            });
          }, 100);
          
          console.groupEnd();
          
          // Throw a specific error that the login page can catch
          reject(new Error('NEW_PASSWORD_REQUIRED'));
        },
        mfaRequired: (challengeName: string, challengeParameters: any) => {
          console.log('=== MFA Required ===');
          console.log('Challenge name:', challengeName);
          console.log('Challenge parameters:', challengeParameters);
          console.groupEnd();
          
          // Ensure we're not authenticated
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          reject(new Error('MFA required'));
        },
        mfaSetup: (challengeName: string, challengeParameters: any) => {
          console.log('=== MFA Setup Required ===');
          console.log('Challenge name:', challengeName);
          console.log('Challenge parameters:', challengeParameters);
          console.groupEnd();
          
          // Ensure we're not authenticated
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          reject(new Error('MFA setup required'));
        },
        totpRequired: (challengeName: string, challengeParameters: any) => {
          console.log('=== TOTP Required ===');
          console.log('Challenge name:', challengeName);
          console.log('Challenge parameters:', challengeParameters);
          console.groupEnd();
          
          // Ensure we're not authenticated
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          reject(new Error('TOTP required'));
        }
      };

      cognitoUser.authenticateUser(authenticationDetails, authCallbacks);
    });
  }

  async completeNewPasswordChallenge(newPassword: string): Promise<boolean> {
    console.group('🔐 AuthService - completeNewPasswordChallenge');
    if (!this.newPasswordRequiredUser) {
      console.log('No user requiring new password');
      console.groupEnd();
      throw new Error('No user requiring new password');
    }

    const user = this.newPasswordRequiredUser;
    return new Promise((resolve, reject) => {
      user.completeNewPasswordChallenge(
        newPassword,
        {},
        {
          onSuccess: async (session: CognitoUserSession) => {
            console.log('=== New Password Challenge Completed ===');
            this.currentCognitoUser = user;
            this.authState.next(true);
            this.lastTokenRefreshTime = Date.now();
            this.newPasswordRequiredUser = null;
            
            // Clear the session storage
            sessionStorage.removeItem('NEW_PASSWORD_REQUIRED');
            sessionStorage.removeItem('USER_ATTRIBUTES');
            
            // Resume session monitoring after successful password change
            this.resumeSessionMonitoring();
            
            // Fetch the user data
            try {
              const userData = await this.getCurrentUser().toPromise();
              this.currentUser = userData || null;
              console.log('User data fetched after password change:', this.currentUser);
              console.groupEnd();
              resolve(true);
            } catch (error) {
              console.error('Error fetching user after password change:', error);
              console.groupEnd();
              reject(error);
            }
          },
          onFailure: (err: Error) => {
            console.error('Failed to complete new password challenge:', err);
            console.groupEnd();
            reject(err);
          }
        }
      );
    });
  }

  async logout(): Promise<void> {
    // Preserve lastUsername from Ionic Storage
    let lastUsername: string | null = null;
    try {
      const storage = new (window as any).Storage();
      storage.create().then(() => {
        storage.get('lastUsername').then((value: string | null) => {
          lastUsername = value;
        });
      });
    } catch (e) { /* ignore */ }
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
    if (this.currentUser) {
      console.log('Logging out user');
      this.currentCognitoUser?.signOut();
      this.currentUser = null;
    }
    this.authState.next(false);
    // Clear all session and local storage
    sessionStorage.clear();
    localStorage.clear();
    // Clear Ionic Storage if used
    if (window && window.indexedDB) {
      if (indexedDB.databases) {
        indexedDB.databases().then((dbs) => {
          dbs.forEach(db => {
            indexedDB.deleteDatabase(db.name!);
          });
        });
      }
    }
    // Restore lastUsername after clearing storage
    if (lastUsername) {
      try {
        const storage = new (window as any).Storage();
        storage.create().then(() => {
          storage.set('lastUsername', lastUsername);
        });
      } catch (e) { /* ignore */ }
    }

    // Allow rotation on the login screen (portrait and landscape)
    try {
      await ScreenOrientation.unlock();
      console.log('Screen orientation unlocked for login screen');
    } catch (error) {
      console.error('Error unlocking screen orientation:', error);
    }

    this.router.navigate(['/login']);
  }

  async isAuthenticated(): Promise<boolean> {
    // Check if we're in a new password required flow
    const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
    if (isNewPasswordRequired) {
      console.log('isAuthenticated: New password required flow detected, returning true');
      return true;
    }
    
    try {
      const session = await this.getCurrentSession();
      const isValid = session !== null && session.isValid();
      
      // Update the auth state subject
      this.authState.next(isValid);
      
      if (isValid) {
        // If we have a valid session but no current user, try to fetch it
        // Don't fail authentication if the API call fails - the user can still be authenticated
        if (!this.currentUser) {
          try {
            const user = await this.getCurrentUser().toPromise();
            this.currentUser = user || null;
          } catch (error) {
            console.error('Error fetching current user:', error);
            // Don't return false here - the user is still authenticated via Cognito
            // The user data will be fetched when needed
          }
        }
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking authentication:', error);
      this.authState.next(false);
      return false;
    }
  }

  getAuthState() {
    return this.authState.asObservable();
  }

  getCurrentUser(): Observable<User> {
    if(this.currentUser) {
      return of(this.currentUser);
    }
    return from(this.getHeaders()).pipe(
      switchMap(headers => {
        return this.http.get<ApiResponse<User>>(`${environment.apiUrl}/users/me`, { headers }).pipe(
          map(response => response.data)
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Updates the cached user snapshot after PUT /users/me returns so callers using
   * `getCurrentUser()` see fresh profile fields (including Cognito-synced avatar color).
   */
  setCurrentUserSnapshot(user: User | null): void {
    this.currentUser = user;
  }

  private handleError(error: HttpErrorResponse) {
    console.error('API Error:', error);
    return throwError(() => error);
  }

  getCurrentCognitoUser(): CognitoUserWithChallenge | null {
    // If we have a stored challenge state, return the user requiring password change
    if (sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true' && this.newPasswordRequiredUser) {
      return this.newPasswordRequiredUser;
    }
    return this.currentCognitoUser;
  }

  async getCurrentSession(): Promise<CognitoUserSession | null> {
    console.group('🔐 Getting Current Session');
    console.log('Step 1: Getting current Cognito user');
    
    return new Promise((resolve, reject) => {
      const cognitoUser = this.userPool.getCurrentUser();
      if (!cognitoUser) {
        console.log('Step 2: No current Cognito user found');
        console.groupEnd();
        resolve(null);
        return;
      }

      console.log('Step 2: Getting session for current user');
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          console.error('Step 3: Error getting session:', err);
          console.groupEnd();
          reject(err);
          return;
        }

        if (!session) {
          console.log('Step 3: No session found');
          console.groupEnd();
          resolve(null);
          return;
        }

        console.log('Step 3: Session found, checking validity');
        const isValid = session.isValid();
        console.log('Session valid:', isValid);
        
        if (!isValid) {
          console.log('Step 4: Session is invalid');
          console.groupEnd();
          resolve(null);
          return;
        }

        console.log('Step 4: Valid session found');
        console.groupEnd();
        resolve(session);
      });
    });
  }

  async getHeaders(): Promise<HttpHeaders> {
    console.group('🔑 Getting API Headers');
    console.log('Step 1: Attempting to get current session');
    
    // Check if we're in a new password required flow
    const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
    if (isNewPasswordRequired) {
      console.log('Step 1.5: New password required flow detected, skipping session check');
      console.groupEnd();
      return new HttpHeaders({
        'Content-Type': 'application/json'
      });
    }
    
    let token = '';
    try {
      const session = await this.getCurrentSession();
      
      if (!session) {
        console.warn('Step 2: No valid session found');
        console.groupEnd();
        return new HttpHeaders({
          'Content-Type': 'application/json'
        });
      }
      
      console.log('Step 2: Session found, extracting token');
      token = session.getIdToken().getJwtToken();
      console.log('Token (first 20 chars):', token.substring(0, 20) + '...');
      
      console.log('Step 3: Creating headers with token');
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });
      
      console.log('Step 4: Headers created successfully');
      console.groupEnd();
      return headers;
    } catch (error) {
      console.error('Step 2: Error getting headers:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
      }
      console.groupEnd();
      return new HttpHeaders({
        'Content-Type': 'application/json'
      });
    }
  }
  
  changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.currentCognitoUser) {
        reject(new Error('No current user'));
        return;
      }
      this.currentCognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          console.error('Change password error:', err);
          reject(err);
        } else {
          console.log('Password changed successfully:', result);
          resolve(true);
        }
      });
    });
  }

  /**
   * Initiates the forgot password flow for a user (sends a code to their email)
   */
  forgotPassword(username: string): Promise<'SUCCESS' | 'ERROR'> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });
      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve('SUCCESS');
        },
        onFailure: (err) => {
          reject(err);
        },
        inputVerificationCode: () => {
          // This will be handled in the UI
          resolve('SUCCESS');
        }
      });
    });
  }

  /**
   * Confirms the new password using the code sent to the user's email
   */
  confirmForgotPassword(username: string, code: string, newPassword: string): Promise<'SUCCESS' | 'ERROR'> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve('SUCCESS');
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  isNewPasswordRequired(): boolean {
    console.group('🔐 AuthService - isNewPasswordRequired');
    console.log('newPasswordRequiredUser:', this.newPasswordRequiredUser);
    const result = this.newPasswordRequiredUser !== null;
    console.log('Result:', result);
    console.groupEnd();
    return result;
  }

  isNewPasswordRequiredChallenge(): boolean {
    return this.newPasswordRequiredUser !== null;
  }

  async getToken(): Promise<string | null> {
    try {
      const session = await this.getCurrentSession();
      if (!session) {
        return null;
      }
      return session.getIdToken().getJwtToken();
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  private setupTokenRefresh() {
    setInterval(async () => {
      // Check if we're in a new password required flow
      const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
      if (isNewPasswordRequired) {
        console.log('Token refresh: New password required flow detected, skipping session check');
        return;
      }
      
      const timeSinceLastRefresh = Date.now() - this.lastTokenRefreshTime;
      if (timeSinceLastRefresh >= this.tokenRefreshInterval) {
        const session = await this.getCurrentSession();
        if (!session) {
          this.handleSessionExpired();
        } else {
          this.lastTokenRefreshTime = Date.now();
        }
      }
    }, 60000); // Check every minute
  }
}
